import { CLASS_LEVELS, MEDIUM_OPTIONS, type Option } from '@/constants/institute';
import type { ClassCategory, ClassRange, ClassSection, ClassTeacher, InstituteClass } from '@/types/instituteClass';
import { CLASS_NAMES, type ClassFormValues } from './schemas/class.schema';

/**
 * Moving a class between the API's shape and the form's, plus the two sums the
 * screen shows while typing. Kept together so the create form, the edit dialog
 * and the roster table all count intake and fees the same way.
 */

/** A half-typed number field ("", "12abc") counts as nothing, never as NaN. */
export function toAmount(value: string | number): number {
  const amount = typeof value === 'number' ? value : Number(value.trim());
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * Seats across a class's sections — what the institute can actually admit, so it
 * counts the maximums. The minimums say when a section is worth running, not
 * how many children fit.
 */
export function totalIntake(sections: { maxSeats: string | number }[]): number {
  return sections.reduce((running, section) => running + toAmount(section.maxSeats), 0);
}

/** Suggests the next letter, so adding sections rarely needs typing. */
export function nextSectionName(sections: { name: string }[]): string {
  const taken = new Set(sections.map((section) => section.name.trim().toUpperCase()));
  for (let index = 0; index < 26; index += 1) {
    const letter = String.fromCharCode(65 + index);
    if (!taken.has(letter)) return letter;
  }
  return '';
}

/**
 * The medium a new class starts on when the institute record names none — the
 * commonest medium in Indian schools. The setup page replaces it with the
 * school's own medium once that has loaded.
 */
export const DEFAULT_MEDIUM = 'ENGLISH';

export const EMPTY_CLASS_FORM: ClassFormValues = {
  name: '',
  code: '',
  // Blank means something: no category is "work it out from the name". The
  // medium is stated from the start — see `DEFAULT_MEDIUM` and the setup page.
  category: '',
  medium: DEFAULT_MEDIUM,
  description: '',
  iconKey: '',
  colourKey: '',
  displayOrder: '',
  // Blank, not guessed: an age rule is the institute's to state, and a class left
  // without one is simply never suggested on the admission form.
  minAgeYears: '',
  maxAgeYears: '',
  sections: [{ name: 'A', minSeats: '1', maxSeats: '' }],
  coreSubjects: [],
  electives: [],
  languages: [],
  tuitionFee: '',
  booksFee: '',
  otherFees: '',
};

const toEntries = (names: string[]) => names.map((name) => ({ name }));

/**
 * `"36000.00"` → `"36000"`. Fees are stored as `DECIMAL` and come back with
 * paise; the form takes whole rupees, and an input showing "36000.00" invites
 * someone to edit it into something the server will reject.
 */
function toWholeRupees(amount: string): string {
  const value = Number(amount);
  return Number.isFinite(value) ? String(Math.round(value)) : '';
}

export function toFormValues(instituteClass: InstituteClass): ClassFormValues {
  return {
    name: instituteClass.name,
    code: instituteClass.code ?? '',
    category: instituteClass.category ?? '',
    medium: instituteClass.medium ?? '',
    description: instituteClass.description ?? '',
    iconKey: instituteClass.iconKey ?? '',
    colourKey: instituteClass.colourKey ?? '',
    displayOrder: String(instituteClass.displayOrder),
    minAgeYears: instituteClass.minAgeYears === null ? '' : String(instituteClass.minAgeYears),
    maxAgeYears: instituteClass.maxAgeYears === null ? '' : String(instituteClass.maxAgeYears),
    sections: instituteClass.sections.map((section) => ({
      name: section.name,
      minSeats: String(section.minSeats),
      maxSeats: String(section.maxSeats),
    })),
    coreSubjects: toEntries(instituteClass.coreSubjects),
    electives: toEntries(instituteClass.electives),
    languages: toEntries(instituteClass.languages),
    tuitionFee: toWholeRupees(instituteClass.tuitionFee),
    booksFee: toWholeRupees(instituteClass.booksFee),
    otherFees: toWholeRupees(instituteClass.otherFees),
  };
}

/** What each field on the class form is called, for the "what is blocking the save" list. */
const FIELD_LABEL: Record<string, string> = {
  name: 'Class name',
  code: 'Class code',
  category: 'Class category',
  medium: 'Medium of instruction',
  description: 'Description',
  iconKey: 'Icon',
  colourKey: 'Colour',
  displayOrder: 'Display order',
  otherFees: 'Other fees',
  minAgeYears: 'Minimum age',
  maxAgeYears: 'Maximum age',
  sections: 'Sections',
  minSeats: 'Min seats',
  maxSeats: 'Max seats',
  coreSubjects: 'Core subjects',
  electives: 'Electives',
  languages: 'Languages',
  tuitionFee: 'Tuition fee',
  booksFee: 'Books and study materials',
  root: '',
};

/**
 * `sections.1.minSeats` → `Sections › 2 › Min seats`.
 *
 * Array indexes are shown counting from one, because "Section 0" means nothing
 * to the person looking at a card labelled "Section 1".
 */
export function describeFieldPath(path: string): string {
  return path
    .split('.')
    .map((part) => {
      if (/^\d+$/.test(part)) return String(Number(part) + 1);
      return FIELD_LABEL[part] ?? part;
    })
    .filter(Boolean)
    .join(' › ');
}

/** Class names are compared case-insensitively — "class 8" is the Class 8. */
export function isNameTaken(names: string[], candidate: string): boolean {
  const wanted = candidate.trim().toLowerCase();
  return names.some((name) => name.trim().toLowerCase() === wanted);
}

/**
 * Every class name the school teaches, in teaching order.
 *
 * `range` is the lowest and highest class chosen when the school was set up.
 * `CLASS_LEVELS` and `CLASS_NAMES` run Nursery to 12 in the same order, so one
 * index serves both. Without a usable range (still loading, or a record that
 * names none) the whole list is offered rather than an empty dropdown.
 *
 * Names already in use are still offered: a list with gaps reads as "Class 1
 * is missing", and the server refuses a second class of the same name anyway.
 *
 * `current` is the name of the class being edited. It is always offered, even
 * when it is not on the list, so a class saved before the list existed — or one
 * outside a range narrowed later — can be edited without being renamed.
 */
export function classNameOptions(current?: string, range?: ClassRange | null): Option[] {
  const levels: readonly string[] = CLASS_LEVELS;
  const from = range ? levels.indexOf(range.lowest) : -1;
  const to = range ? levels.indexOf(range.highest) : -1;
  const offered: string[] =
    from >= 0 && to >= from ? CLASS_NAMES.slice(from, to + 1) : [...CLASS_NAMES];

  const names = current && !offered.includes(current) ? [current, ...offered] : offered;
  return names.map((name) => ({ value: name, label: name }));
}

/**
 * The mediums the school teaches in, as chosen when it was set up — not every
 * language the product knows. Falls back to the full list while the school's
 * own has not loaded, or for a record that names none.
 *
 * `current` is a class's saved medium. It stays on offer even when the school
 * no longer lists it, so editing the class does not silently change it.
 */
export function schoolMediumOptions(mediums: string[] | undefined, current?: string): Option[] {
  if (!mediums || mediums.length === 0) return MEDIUM_OPTIONS;

  const offered = MEDIUM_OPTIONS.filter((option) => mediums.includes(option.value));
  const kept = [...mediums, current ?? ''].filter(
    (value) => value && !offered.some((option) => option.value === value),
  );
  return [
    ...offered,
    ...kept.map((value) => MEDIUM_OPTIONS.find((option) => option.value === value) ?? { value, label: value }),
  ];
}

/**
 * Classes in teaching order — Nursery, LKG, UKG, Class 1 … Class 12 — or the
 * reverse. A name off the standard list (saved before the list existed) goes
 * after every standard one, by its display order and then its name, whichever
 * way the list runs.
 */
export function sortByTeachingOrder<T extends { name: string; displayOrder: number }>(
  classes: T[],
  order: 'LOWEST_FIRST' | 'HIGHEST_FIRST',
): T[] {
  const names: readonly string[] = CLASS_NAMES;
  const rank = (name: string) => {
    const index = names.indexOf(name);
    return index === -1 ? Number.POSITIVE_INFINITY : index;
  };
  const direction = order === 'HIGHEST_FIRST' ? -1 : 1;

  return [...classes].sort((a, b) => {
    const rankA = rank(a.name);
    const rankB = rank(b.name);
    if (rankA !== rankB) {
      if (rankA === Number.POSITIVE_INFINITY) return 1;
      if (rankB === Number.POSITIVE_INFINITY) return -1;
      return (rankA - rankB) * direction;
    }
    return a.displayOrder - b.displayOrder || a.name.localeCompare(b.name);
  });
}

/** Sections as the table renders them: "A · 20–35". */
export function describeSection(section: ClassSection): string {
  return `${section.name} · ${section.minSeats}–${section.maxSeats}`;
}

/** The stages of school, youngest first — the order the summary lists them in. */
export const CLASS_STAGES = [
  'Pre-Primary',
  'Primary',
  'Middle',
  'Secondary',
  'Higher Secondary',
] as const;
export type ClassStage = (typeof CLASS_STAGES)[number];

/**
 * Which stage of school a class belongs to.
 *
 * Read off the *name*, because that is all `GET /institute/classes` carries —
 * there is no stage column behind it. An institute naming its classes something
 * this cannot place simply gets "Primary", which is a safe answer rather than a
 * wrong one.
 *
 * Lives here rather than in the page because the roster's badge, its stage
 * filter and the academic summary must all place a class the same way — three
 * copies of this ladder is three chances for the table and the summary beneath
 * it to disagree (CLAUDE.md §3).
 */
export function stageOf(name: string): ClassStage {
  const lower = name.toLowerCase();

  if (/lkg|ukg|nursery|pre/.test(lower)) return 'Pre-Primary';

  const grade = gradeNumberOf(lower);
  if (grade === null) return 'Primary';
  if (grade >= 11) return 'Higher Secondary';
  if (grade >= 9) return 'Secondary';
  if (grade >= 6) return 'Middle';
  return 'Primary';
}

const ROMAN_GRADE: Record<string, number> = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
  vi: 6,
  vii: 7,
  viii: 8,
  ix: 9,
  x: 10,
  xi: 11,
  xii: 12,
};

/**
 * The grade a class name points at — "Class 9", "9th", "Std IX" all give 9.
 *
 * The ordinal suffix is allowed for explicitly: a plain word-boundary match
 * misses "9th", which is how a 9th class came to be placed in Primary.
 */
function gradeNumberOf(lower: string): number | null {
  const digits = /\b(\d{1,2})(?:st|nd|rd|th)?\b/.exec(lower);
  if (digits) return Number(digits[1]);

  const roman = /\b(xii|xi|ix|x|viii|vii|vi|iv|v|iii|ii|i)\b/.exec(lower);
  return roman ? (ROMAN_GRADE[roman[1]] ?? null) : null;
}

/**
 * A class name as the roster shows it: "Class - 9".
 *
 * Institutes type their classes every way — "Class 9", "9th", "Std IX" — so a
 * name that is *only* a grade is normalised to one form. Anything else is left
 * exactly as typed: "Nursery" and "LKG" have no number, and "Class 11 Science"
 * says more than its grade, so cutting it down would lose what the office wrote.
 */
export function classDisplayName(name: string): string {
  const grade = /^(?:class|std\.?|standard|grade)?\s*-?\s*(\d{1,2}|xii|xi|ix|x|viii|vii|vi|iv|v|iii|ii|i)(?:st|nd|rd|th)?$/i.exec(
    name.trim(),
  );
  if (!grade) return name;

  const value = grade[1].toLowerCase();
  const number = /^\d+$/.test(value) ? Number(value) : ROMAN_GRADE[value];
  return number ? `Class - ${number}` : name;
}

/**
 * How each stage is tinted, so a badge and a summary row always agree.
 *
 * A *ramp*, not four unrelated hues: the stages are ordered — a child climbs
 * through them — so the colour deepens with them, sky through blue to indigo.
 * That keeps all four inside the console's own blue (see
 * `features/institute/theme.ts`) while still telling them apart, which four
 * arbitrary colours did only by being loud.
 *
 * Colour is never the only thing saying it: every badge carries the stage's
 * name and every summary row its counts.
 */
export const STAGE_TONE: Record<ClassStage, { badge: string; bar: string; dot: string }> = {
  'Pre-Primary': {
    badge:
      'bg-amber-50/90 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200/80 dark:border-amber-800/60',
    bar: 'bg-gradient-to-r from-amber-400 to-amber-500',
    dot: 'bg-amber-500',
  },
  Primary: {
    badge:
      'bg-indigo-50/90 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-300 border-indigo-200/80 dark:border-indigo-800/60',
    bar: 'bg-gradient-to-r from-indigo-400 to-indigo-600',
    dot: 'bg-indigo-500',
  },
  Middle: {
    badge:
      'bg-emerald-50/90 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/60',
    bar: 'bg-gradient-to-r from-emerald-400 to-emerald-600',
    dot: 'bg-emerald-500',
  },
  Secondary: {
    badge:
      'bg-blue-50/90 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200/80 dark:border-blue-800/60',
    bar: 'bg-gradient-to-r from-blue-400 to-blue-600',
    dot: 'bg-blue-500',
  },
  'Higher Secondary': {
    badge:
      'bg-purple-50/90 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300 border-purple-200/80 dark:border-purple-800/60',
    bar: 'bg-gradient-to-r from-purple-400 to-purple-600',
    dot: 'bg-purple-500',
  },
};

/** What the category enum is called on the ladder above. */
const CATEGORY_STAGE: Record<ClassCategory, ClassStage> = {
  PRE_PRIMARY: 'Pre-Primary',
  PRIMARY: 'Primary',
  MIDDLE: 'Middle',
  SECONDARY: 'Secondary',
  HIGHER_SECONDARY: 'Higher Secondary',
};

/**
 * Which band of school a class belongs to.
 *
 * What the institute stated, and only failing that what the name suggests. Every
 * screen that places a class should call this rather than `stageOf` — the guess
 * is right most of the time and wrong for every institute that names its classes
 * "Std VIII" or "Grade R", which is exactly why the column now exists.
 */
export function stageOfClass(instituteClass: {
  name: string;
  category?: ClassCategory | null;
}): ClassStage {
  return instituteClass.category
    ? CATEGORY_STAGE[instituteClass.category]
    : stageOf(instituteClass.name);
}

/**
 * One row of the roster: a section of a class, with its own class teacher.
 *
 * The roster lists 9-A and 9-B on rows of their own because each section is run
 * by a different class teacher. One row per class could name only one of them,
 * and filtering by 9-B's teacher then showed 9-A's. A class with no sections yet
 * keeps a single row, with `section` null.
 */
export interface ClassSectionRow {
  /** Unique across the roster — the section's uuid, or the class's when it has none. */
  key: string;
  instituteClass: InstituteClass;
  section: ClassSection | null;
  /** This section's class teacher. Null when nobody has been allotted. */
  classTeacher: ClassTeacher | null;
}

/** A class's rows, in the institute's own section order. */
export function toSectionRows(instituteClass: InstituteClass): ClassSectionRow[] {
  if (instituteClass.sections.length === 0) {
    return [{ key: instituteClass.uuid, instituteClass, section: null, classTeacher: null }];
  }

  return [...instituteClass.sections]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((section) => ({
      key: section.uuid,
      instituteClass,
      section,
      // Matched by name: that is how the class teacher list names its section.
      classTeacher:
        instituteClass.classTeachers.find((teacher) => teacher.sectionName === section.name) ?? null,
    }));
}
